import { api } from '@/api/client';

export { calculateBundles, piecesInBundle, routeTypeFromCode, routeTypeLabel } from '@eddm/shared';

/** Route for a page name, matching the paths registered in App.jsx. */
export function createPageUrl(pageName) {
  return `/${String(pageName).toLowerCase().replace(/\s+/g, '-')}`;
}

/**
 * Record an operator action.
 *
 * Kept as a helper so screens can call it the way they did under Base44, but
 * the write itself happens server-side: the browser has no insert rights on
 * activity_log, which is what stops the audit trail being edited from a console.
 */
export async function logActivity(...args) {
  // Two shapes are accepted. The object form is the real one:
  //   logActivity({ mailing_id, type, description })
  // The positional form is what the Base44 screens used:
  //   logActivity(mailingId, mailingName, orderNumber, type, description, user)
  // Its middle arguments are ignored — the server looks the mailing name and
  // order number up itself, and takes the actor from the session, because an
  // audit entry that lets the caller name themselves is not an audit entry.
  const entry =
    typeof args[0] === 'object' && args[0] !== null
      ? args[0]
      : { mailing_id: args[0] ?? null, type: args[3], description: args[4] };

  if (!entry.type || !entry.description) {
    console.warn('logActivity called without a type or description', args);
    return;
  }

  try {
    await api.activity.record(entry);
  } catch (err) {
    // Never let an audit write break the operation it was recording.
    console.error('Failed to record activity:', err);
  }
}

/**
 * Escape one CSV cell.
 *
 * A leading `=`, `+`, `-` or `@` makes Excel and Sheets treat the cell as a
 * formula. Mailing names are free text typed by staff or pasted from a customer
 * email, and this export is imported into the billing system, so a name
 * beginning with one of those characters was a live formula-injection path.
 */
export function csvCell(value) {
  const text = value === null || value === undefined ? '' : String(value);

  // Prefixing with an apostrophe keeps the text visible while stopping
  // evaluation. Wrapping in quotes alone does not: Excel still evaluates it.
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;

  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/** Build a CSV document from a header row and body rows. */
export function toCsv(headers, rows) {
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

/** Trigger a download of text content. */
export function downloadFile(filename, content, mimeType = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** `2026-08-13` rendered as `8/13/2026`, without the UTC day-shift. */
export function formatDate(value) {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (year && month && day) return `${Number(month)}/${Number(day)}/${year}`;
  return value;
}

export function formatMoney(value) {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : '$0.00';
}
